import axios from 'axios';
import type { QueryClient } from '@tanstack/react-query';

/**
 * The account this tab uses, when the browser is signed in to several
 * (docs/MultiAccountSessions.md §3, §9).
 *
 * Per tab, like the active context: kept in `sessionStorage` and sent on every request as
 * `X-Active-Account-Id`, and the backend picks that account among the auth cookies this
 * browser holds. **Choosing authenticates nothing**: every account was added by a full
 * sign-in, and naming one the browser doesn't hold is refused (`account_signed_out`).
 */

export const ACCOUNT_HEADER = 'X-Active-Account-Id';
/** The parameter for requests that can't send a header: the socket, downloads, links. */
export const ACCOUNT_PARAM = 'account';
/** The most accounts one browser holds; the backend refuses a sixth sign-in. */
export const MAX_ACCOUNTS = 5;

const STORAGE_KEY = 'activeAccountId';
// The tab's context belongs to its account; a switch must not carry it across.
const CONTEXT_STORAGE_KEY = 'activeContextId';
// Set while a link's `?account=` is unconfirmed, so an account this browser doesn't hold
// reads as "sign in to open this link" rather than as falling back to another account.
const LINK_HINT_KEY = 'activeAccountFromLink';

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function getActiveAccountId(): string | null {
  return storage()?.getItem(STORAGE_KEY) ?? null;
}

/** Point this tab's requests at `id`, or at the browser's first account with `null`. */
export function setActiveAccountId(id: string | null): void {
  const s = storage();
  if (id) {
    s?.setItem(STORAGE_KEY, id);
    axios.defaults.headers.common[ACCOUNT_HEADER] = id;
  } else {
    s?.removeItem(STORAGE_KEY);
    delete axios.defaults.headers.common['X-Active-Account-Id'];
  }
  s?.removeItem(LINK_HINT_KEY);
}

/** Whether this tab's account came from a link and the backend hasn't confirmed it yet. */
export function accountCameFromLink(): boolean {
  return storage()?.getItem(LINK_HINT_KEY) === '1';
}

/**
 * Switch this tab to another account the browser holds (decision 10): a full page load as
 * that account. One load resets everything held in memory for the old one: the query cache,
 * requests parked by the session-expired dialog (which would replay as the old account), the
 * suspension lockout, the router and the socket.
 */
export function switchAccount(id: string | null, to?: string): void {
  setActiveAccountId(id);
  storage()?.removeItem(CONTEXT_STORAGE_KEY);
  window.location.assign(to ?? `${window.location.pathname}${window.location.search}${window.location.hash}`);
}

/** `url` carrying this tab's account, for a request that can't send the header. */
export function withAccount(url: string): string {
  const id = getActiveAccountId();
  if (!id) return url;
  const hash = url.indexOf('#');
  const path = hash < 0 ? url : url.slice(0, hash);
  const fragment = hash < 0 ? '' : url.slice(hash);
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${ACCOUNT_PARAM}=${encodeURIComponent(id)}${fragment}`;
}

/**
 * A `localStorage` key for something that belongs to the person's account rather than the
 * browser: dismissals, remembered choices (docs/MultiAccountSessions.md §9). Theme and the
 * install prompt stay per browser and don't use this.
 */
export function accountScoped(key: string): string {
  const id = getActiveAccountId();
  return id ? `${key}:${id}` : key;
}

/** The page a sign-in continues to: the sign-in page's `returnTo`, else where it happened. */
function continuation(): string {
  const { pathname, search, hash } = window.location;
  if (pathname === '/signin') {
    const returnTo = new URLSearchParams(search).get('returnTo');
    return returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/home';
  }
  return `${pathname}${search}${hash}`;
}

/**
 * Finish any sign-in: select the account it signed in as (the response's `id`), then carry
 * on. A tab that was using a different account, or that is adding one from the account menu,
 * loads `destination` (by default the sign-in page's `returnTo`, or the page it happened on)
 * afresh as the new account, and this resolves `true`. Otherwise `currentUser` is refreshed in
 * place, as before, and the caller navigates as it always has.
 */
export async function completeSignIn(queryClient: QueryClient, response: unknown, destination?: string): Promise<boolean> {
  const id = (response as { id?: unknown } | null)?.id;
  const previous = getActiveAccountId();
  const adding = window.location.pathname === '/signin' && new URLSearchParams(window.location.search).get('add') === '1';
  if (typeof id === 'string') {
    setActiveAccountId(id);
    if ((previous && previous !== id) || adding) {
      storage()?.removeItem(CONTEXT_STORAGE_KEY);
      window.location.assign(destination ?? continuation());
      return true;
    }
  }
  await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
  return false;
}

/**
 * A link's `?account=` (an SSO landing, and later email and push links, §6): select that
 * account for this tab and strip the parameter so it isn't bookmarked or re-shared.
 */
function adoptLinkHint(): void {
  const params = new URLSearchParams(window.location.search);
  const hinted = params.get(ACCOUNT_PARAM);
  if (hinted === null) return;
  params.delete(ACCOUNT_PARAM);
  if (hinted && hinted !== getActiveAccountId()) {
    setActiveAccountId(hinted);
    storage()?.removeItem(CONTEXT_STORAGE_KEY);
    storage()?.setItem(LINK_HINT_KEY, '1');
  }
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
}

/**
 * Answer the service worker when it asks which account this tab uses. A click on a push
 * notification goes to a tab already on the notification's account, so nothing switches
 * under the user (docs/MultiAccountSessions.md §8, `public/sw.js`).
 */
function answerServiceWorker(event: MessageEvent): void {
  if ((event.data as { type?: unknown } | null)?.type !== 'which-account') return;
  event.ports.at(0)?.postMessage({ account: getActiveAccountId() });
}

// At module scope, before the first request leaves: `ActiveContextProvider` mounts only after
// `/api/auth/current` has answered, and that first request has to name the account already.
adoptLinkHint();
const stored = getActiveAccountId();
if (stored) {
  axios.defaults.headers.common[ACCOUNT_HEADER] = stored;
}
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', answerServiceWorker);
}
